import { useEffect } from "react";

/**
 * 1NOW-11223: on iOS Safari a quick or double tap selects a button's label, and when the label turns into the
 * loading spinner the selection stays painted inside the button as a grey/blue bar. For a box of buttons (the
 * booking sheet, its bottom bar, the overview card): no text selection, no long-press callout and no double-tap
 * zoom on any button in it. Inputs are left alone (a non-selectable ancestor can stop iOS typing into them).
 */
export const NO_TAP_SELECT =
  "[&_button]:select-none [&_button]:[-webkit-user-select:none] [&_button]:[-webkit-touch-callout:none] [&_button]:[touch-action:manipulation] " +
  // The reopen (8 Oct, still on Safari): iOS also paints its grey tap highlight over the tapped button, and Safari
  // draws its focus ring after a tap; both stay while the label swaps to the spinner. A keyboard focus keeps its ring.
  "[&_button]:[-webkit-tap-highlight-color:transparent] [&_button:focus:not(:focus-visible)]:outline-none";

/**
 * The third reopen (8 Oct): "Pay and book now" went grey under the finger. Not a hover style: the button is
 * disabled while the booking is made, and `disabled:opacity-60` turned Design B's dark button grey for those
 * seconds. A busy button keeps its colour and shows only the spinner; a button that just waits still fades.
 */
export const fadeUnlessBusy = (busy?: boolean) => (busy ? "" : "disabled:opacity-60");

/** The main button's hover: a touch lighter, in its own colour, and only where there is a real pointer (iOS keeps
 *  :hover after a tap). */
export const MAIN_HOVER = "[@media(hover:hover)]:enabled:hover:opacity-90";

/** And a selection made before the switch (the tap that started it) is cleared when a button starts loading. */
export const useClearSelectionWhen = (loading: boolean | undefined) =>
  useEffect(() => {
    if (loading) window.getSelection()?.removeAllRanges();
  }, [loading]);
